import React, { useEffect, useState } from 'react'
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Close } from '@mui/icons-material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { useSnackbar } from 'notistack'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import moment from 'moment'

const PREDEFINED_COLORS = [
    '#3B82F6',
    '#22C55E',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#EC4899',
    '#06B6D4',
    '#14B8A6',
]

const INITIAL_FORM = {
    nickname: '',
    bank_code: '',
    bank_name: '',
    bank_branch: '',
    bank_branch_digit: '',
    bank_account: '',
    bank_account_digit: '',
    account_type: 'checking',
    holder_name: '',
    holder_document: '',
    initial_balance: '',
    initial_balance_date: null,
    color: '#3B82F6',
    is_active: true,
}

const BankAccountFormModal = ({ open, onClose, editData, onSave }) => {
    const { enqueueSnackbar } = useSnackbar()
    const [form, setForm] = useState(INITIAL_FORM)
    const [errors, setErrors] = useState({})
    const [saving, setSaving] = useState(false)
    const [bankOptions, setBankOptions] = useState([])
    const [selectedBank, setSelectedBank] = useState(null)

    useEffect(() => {
        if (open) {
            api.get('fitbank/bank-choices/')
                .then((res) => setBankOptions(res.data || []))
                .catch(() => {})
        }
    }, [open])

    useEffect(() => {
        if (editData) {
            setForm({
                nickname: editData.nickname || '',
                bank_code: editData.bank_code || '',
                bank_name: editData.bank_name || '',
                bank_branch: editData.bank_branch || '',
                bank_branch_digit: editData.bank_branch_digit || '',
                bank_account: editData.bank_account || '',
                bank_account_digit: editData.bank_account_digit || '',
                account_type: editData.account_type || 'checking',
                holder_name: editData.holder_name || '',
                holder_document: editData.holder_document || '',
                initial_balance: editData.initial_balance ?? '',
                initial_balance_date: editData.initial_balance_date
                    ? new Date(editData.initial_balance_date)
                    : null,
                color: editData.color || '#3B82F6',
                is_active: editData.is_active ?? true,
            })
            const bank = bankOptions.find((b) => b.code === editData.bank_code)
            setSelectedBank(bank || null)
        } else {
            setForm(INITIAL_FORM)
            setSelectedBank(null)
        }
        setErrors({})
    }, [editData, open, bankOptions])

    const handleChange = (e) => {
        const { name, value } = e.target
        setForm((prev) => ({ ...prev, [name]: value }))
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }))
    }

    const validate = () => {
        const newErrors = {}
        if (!form.nickname.trim()) newErrors.nickname = 'Apelido é obrigatório'
        if (!form.bank_code) newErrors.bank_code = 'Selecione um banco'
        if (!form.bank_branch.trim()) newErrors.bank_branch = 'Agência é obrigatória'
        if (!form.bank_account.trim()) newErrors.bank_account = 'Conta é obrigatória'
        if (!form.holder_name.trim()) newErrors.holder_name = 'Nome do titular é obrigatório'
        if (!form.holder_document.trim()) newErrors.holder_document = 'CPF/CNPJ é obrigatório'

        if (form.initial_balance && !form.initial_balance_date) {
            newErrors.initial_balance_date = 'Data é obrigatória quando saldo inicial é informado'
        }
        if (form.initial_balance_date && !form.initial_balance) {
            newErrors.initial_balance = 'Valor é obrigatório quando data de saldo é informada'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSave = () => {
        if (!validate()) return

        setSaving(true)
        const payload = {
            condo: getCondoId(),
            nickname: form.nickname,
            bank_code: form.bank_code,
            bank_name: form.bank_name,
            bank_branch: form.bank_branch,
            bank_branch_digit: form.bank_branch_digit || '',
            bank_account: form.bank_account,
            bank_account_digit: form.bank_account_digit || '',
            account_type: form.account_type,
            holder_name: form.holder_name,
            holder_document: form.holder_document,
            color: form.color,
            is_active: form.is_active,
        }

        if (form.initial_balance) {
            payload.initial_balance = parseFloat(form.initial_balance)
        }
        if (form.initial_balance_date) {
            payload.initial_balance_date = moment(form.initial_balance_date).format('YYYY-MM-DD')
        }

        const request = editData
            ? api.patch(`cashflow/managed-bank-accounts/${editData.id}/`, payload)
            : api.post('cashflow/managed-bank-accounts/', payload)

        request
            .then(() => {
                enqueueSnackbar(
                    editData ? 'Conta atualizada com sucesso!' : 'Conta criada com sucesso!',
                    { variant: 'success' }
                )
                onSave()
                onClose()
            })
            .catch((err) => {
                const data = err?.response?.data
                if (data && typeof data === 'object') {
                    const firstKey = Object.keys(data)[0]
                    const msg = Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey]
                    enqueueSnackbar(msg || 'Erro ao salvar conta', { variant: 'error' })
                } else {
                    enqueueSnackbar('Erro ao salvar conta', { variant: 'error' })
                }
            })
            .finally(() => setSaving(false))
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {editData ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
                    </Typography>
                    {editData?.data_source && (
                        <Chip
                            label={editData.data_source === 'automatic' ? 'Integrada' : 'Manual'}
                            size="small"
                            sx={{
                                bgcolor: editData.data_source === 'automatic' ? '#22C55E14' : '#3B82F614',
                                color: editData.data_source === 'automatic' ? '#22C55E' : '#3B82F6',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 24,
                                border: `1px solid ${editData.data_source === 'automatic' ? '#22C55E30' : '#3B82F630'}`,
                            }}
                        />
                    )}
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Apelido"
                            name="nickname"
                            value={form.nickname}
                            onChange={handleChange}
                            error={!!errors.nickname}
                            helperText={errors.nickname}
                            placeholder="Ex: Itaú Principal"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            options={bankOptions}
                            getOptionLabel={(opt) =>
                                opt ? `${opt.code} - ${opt.name}` : ''
                            }
                            value={selectedBank}
                            onChange={(_, val) => {
                                setSelectedBank(val)
                                setForm((prev) => ({
                                    ...prev,
                                    bank_code: val?.code || '',
                                    bank_name: val?.name || '',
                                }))
                                if (errors.bank_code) setErrors((prev) => ({ ...prev, bank_code: '' }))
                            }}
                            isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Banco"
                                    size="small"
                                    error={!!errors.bank_code}
                                    helperText={errors.bank_code}
                                    placeholder="Buscar banco..."
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Agência"
                            name="bank_branch"
                            value={form.bank_branch}
                            onChange={handleChange}
                            error={!!errors.bank_branch}
                            helperText={errors.bank_branch}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Dígito"
                            name="bank_branch_digit"
                            value={form.bank_branch_digit}
                            onChange={handleChange}
                            inputProps={{ maxLength: 2 }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Conta"
                            name="bank_account"
                            value={form.bank_account}
                            onChange={handleChange}
                            error={!!errors.bank_account}
                            helperText={errors.bank_account}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Dígito"
                            name="bank_account_digit"
                            value={form.bank_account_digit}
                            onChange={handleChange}
                            inputProps={{ maxLength: 2 }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label="Tipo de Conta"
                            name="account_type"
                            value={form.account_type}
                            onChange={handleChange}
                        >
                            <MenuItem value="checking">Conta Corrente</MenuItem>
                            <MenuItem value="savings">Conta Poupança</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography
                            variant="caption"
                            sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                        >
                            Titular
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nome do Titular"
                            name="holder_name"
                            value={form.holder_name}
                            onChange={handleChange}
                            error={!!errors.holder_name}
                            helperText={errors.holder_name}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="CPF/CNPJ"
                            name="holder_document"
                            value={form.holder_document}
                            onChange={handleChange}
                            error={!!errors.holder_document}
                            helperText={errors.holder_document}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography
                            variant="caption"
                            sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                        >
                            Saldo Inicial (opcional)
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Valor"
                            name="initial_balance"
                            type="number"
                            value={form.initial_balance}
                            onChange={handleChange}
                            error={!!errors.initial_balance}
                            helperText={errors.initial_balance}
                            inputProps={{ step: '0.01' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DesktopDatePicker
                            label="Data de Referência"
                            value={form.initial_balance_date}
                            onChange={(date) => {
                                setForm((prev) => ({ ...prev, initial_balance_date: date }))
                                if (errors.initial_balance_date)
                                    setErrors((prev) => ({ ...prev, initial_balance_date: '' }))
                            }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    error: !!errors.initial_balance_date,
                                    helperText: errors.initial_balance_date,
                                },
                            }}
                            format="dd/MM/yyyy"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography
                            variant="caption"
                            sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block' }}
                        >
                            Cor
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {PREDEFINED_COLORS.map((c) => (
                                <Box
                                    key={c}
                                    onClick={() => setForm((prev) => ({ ...prev, color: c }))}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        bgcolor: c,
                                        cursor: 'pointer',
                                        border:
                                            form.color === c
                                                ? '3px solid #1E293B'
                                                : '2px solid #E2E8F0',
                                        transition: 'border 0.15s',
                                        '&:hover': { border: '3px solid #94A3B8' },
                                    }}
                                />
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>
                    Cancelar
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={saving}
                    onClick={handleSave}
                    sx={{
                        bgcolor: '#3B82F6',
                        '&:hover': { bgcolor: '#2563EB' },
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                    }}
                >
                    Salvar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default BankAccountFormModal
